import { T, useGT } from 'gt-react';
import { useNavigate } from '@tanstack/react-router';
import { PlusIcon } from '@phosphor-icons/react';
import type { ApExceptionRuleSet } from '@erp/rpc';
import { CoreEnumPicker } from 'src/components/core/CoreEnumPicker';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import { HeaderPageTitle } from 'src/context/header-title.context';
import { useToast } from 'src/context/ToastContext';
import { ExceptionRuleEditorRow } from './ExceptionRuleEditorRow';
import { ExceptionTypeCatalog } from './ExceptionTypeCatalog';
import { applyExampleGuidance } from './exampleDetectionGuidance';
import { takenTaskTypeKeys, type RuleSetDraft } from './exceptionRuleSetDraft';
import { TestRuleSetOnInvoicesAction } from './TestRuleSetOnInvoicesDialog';
import { useRuleSetEditor } from './useRuleSetEditor';

/**
 * Create / edit one exception rule set: name, description, the exception
 * vocabulary (a Core Enum picked or created inline), and the ordered rules.
 * Rule order is priority — the first rule whose type fires owns the invoice.
 */
export function ExceptionRuleSetEditorPage({
  ruleSet,
  routeId,
}: {
  /** Null = create a new set. */
  ruleSet: ApExceptionRuleSet | null;
  /** Route whose crumb the live name replaces. */
  routeId: string;
}) {
  const gt = useGT();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const editor = useRuleSetEditor(ruleSet);

  function goBack() {
    if (ruleSet) {
      void navigate({
        to: '/accounts-payable/exception-rules/$ruleSetId',
        params: { ruleSetId: ruleSet.id },
      });
      return;
    }
    void navigate({ to: '/accounts-payable/exception-rules' });
  }

  async function handleSave() {
    try {
      const savedId = await editor.save();
      showToast({
        title: gt('Rule set saved', {
          context: 'exception rules: toast title after saving a rule set',
        }),
        color: 'success',
        icon: 'check',
      });
      if (savedId) {
        void navigate({
          to: '/accounts-payable/exception-rules/$ruleSetId',
          params: { ruleSetId: savedId },
        });
        return;
      }
      goBack();
    } catch (error) {
      showToast({
        title: gt('Could not save rule set', {
          context: 'exception rules: toast title when saving fails',
        }),
        message: error instanceof Error ? error.message : String(error),
        color: 'error',
        icon: 'error',
      });
    }
  }

  if (editor.enumsError) {
    return (
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-destructive">
          <T context="exception rules: core enums failed to load">
            Could not load exception types. Retry to continue editing.
          </T>
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void editor.refetchEnums()}
        >
          <T context="exception rules: retry loading core enums">Retry</T>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <HeaderPageTitle routeId={routeId}>
            {editor.draft.name.trim() ||
              (ruleSet ? (
                <T context="exception rules: page title when editing">
                  Edit Exception Rule Set
                </T>
              ) : (
                <T context="exception rules: page title when creating">
                  New Exception Rule Set
                </T>
              ))}
          </HeaderPageTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            <T context="exception rules: editor page description">
              Pick the exception vocabulary, then order the rules by priority.
              When an invoice raises several exceptions, the first matching rule
              names its owner. Types without a rule still open the exception,
              unassigned.
            </T>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={editor.draft.rules.length === 0}
            onClick={() => {
              const typeNameById = new Map(
                editor.typeOptions.map((option) => [option.id, option.name]),
              );
              editor.setDraft({
                ...editor.draft,
                rules: applyExampleGuidance(editor.draft.rules, typeNameById),
              });
            }}
          >
            <T context="exception rules: fill example detection guidance">
              Fill example guidance
            </T>
          </Button>
          <TestRuleSetOnInvoicesAction
            draft={editor.draft}
            savedRuleSetId={ruleSet?.id ?? null}
          />
          <Button type="button" variant="outline" onClick={goBack}>
            <T context="exception rules: cancel button">Cancel</T>
          </Button>
          <Button
            type="button"
            disabled={!editor.canSave}
            onClick={() => void handleSave()}
          >
            {editor.saving ? (
              <T context="exception rules: save button while saving">Saving…</T>
            ) : (
              <T context="exception rules: save button">Save Rule Set</T>
            )}
          </Button>
        </div>
      </div>

      {editor.enumsLoading && (
        <p className="text-sm text-muted-foreground">
          <T context="exception rules: loading core enums">
            Loading exception types…
          </T>
        </p>
      )}

      <div className="space-y-2">
        <RuleSetFields
          draft={editor.draft}
          disabled={editor.saving}
          onChange={editor.setDraft}
          onEnumChange={editor.setEnum}
        />
        {editor.draft.exceptionEnumId ? (
          <div className="grid gap-2 lg:grid-cols-[minmax(18rem,24rem)_minmax(0,1fr)]">
            <ExceptionTypeCatalog
              options={editor.typeOptions}
              rules={editor.draft.rules}
              disabled={editor.saving}
              onAddRule={editor.addRule}
              onAddRemaining={editor.addRemainingRules}
            />
            <RulesList editor={editor} />
          </div>
        ) : (
          <RulesList editor={editor} />
        )}
      </div>
    </div>
  );
}

function RuleSetFields({
  draft,
  disabled,
  onChange,
  onEnumChange,
}: {
  draft: RuleSetDraft;
  disabled: boolean;
  onChange: (next: RuleSetDraft) => void;
  onEnumChange: (enumId: string | null) => void;
}) {
  const gt = useGT();
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="exception-rule-set-name" className="text-xs">
            <T context="exception rules: name field label">Name</T>
          </Label>
          <Input
            id="exception-rule-set-name"
            value={draft.name}
            disabled={disabled}
            maxLength={120}
            onChange={(event) =>
              onChange({ ...draft, name: event.target.value })
            }
            placeholder={gt('e.g. Pennrose AP exceptions', {
              context: 'exception rules: name placeholder',
            })}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">
            <T context="exception rules: enum picker label">
              Exception types (Core Enum)
            </T>
          </Label>
          <CoreEnumPicker
            value={draft.exceptionEnumId}
            onChange={onEnumChange}
            allowNone={false}
            disabled={disabled}
            className="space-y-1"
            createDescription={
              <T context="exception rules: description inside the inline create-enum dialog">
                Each option is an exception type the evaluator may raise on an
                invoice. The option description is shown to people; per-rule
                detection guidance is what steers the evaluator.
              </T>
            }
            optionNamePlaceholder={gt('e.g. Duplicate invoice number', {
              context: 'exception rules: enum option name placeholder',
            })}
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="exception-rule-set-description" className="text-xs">
          <T context="exception rules: description field label">
            Description (optional)
          </T>
        </Label>
        <Textarea
          id="exception-rule-set-description"
          value={draft.description}
          disabled={disabled}
          rows={2}
          maxLength={2000}
          onChange={(event) =>
            onChange({ ...draft, description: event.target.value })
          }
        />
      </div>
    </>
  );
}

function RulesList({
  editor,
}: {
  editor: ReturnType<typeof useRuleSetEditor>;
}) {
  const { draft, saving } = editor;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">
          <T context="exception rules: rules list heading">
            Rules (highest priority first — put a catch-all unmatched type last)
          </T>
        </Label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={saving || !editor.canAddRule}
          onClick={() => editor.addRule()}
        >
          <PlusIcon className="mr-1 h-4 w-4" />
          <T context="exception rules: add rule button">Add Rule</T>
        </Button>
      </div>
      {draft.rules.length === 0 && (
        <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
          {draft.exceptionEnumId ? (
            <T context="exception rules: empty rules state with an enum selected">
              No rules yet. Exceptions will open unassigned until a rule names
              an owner for their type.
            </T>
          ) : (
            <T context="exception rules: empty rules state without an enum">
              Pick or create the exception types first, then add rules.
            </T>
          )}
        </p>
      )}
      {draft.rules.map((rule, index) => (
        <ExceptionRuleEditorRow
          key={rule.key}
          index={index}
          total={draft.rules.length}
          rule={rule}
          typeOptions={editor.typeOptionsFor(rule)}
          isStale={editor.staleKeys.has(rule.key)}
          takenTaskTypeKeys={takenTaskTypeKeys(draft.rules, rule)}
          disabled={saving}
          onChange={editor.updateRule}
          onMove={(direction) => editor.moveRuleAt(index, direction)}
          onRemove={() => editor.removeRule(rule.key)}
        />
      ))}
    </div>
  );
}
